type CustomAppConfig = {
  auth: {
    // List of layouts that are accessible ONLY by guests
    guestsLayouts: string[]

    // List of pages that are accessible ONLY by guests
    guestsPages: string[]

    // The path to redirect to after the user impersonates another user
    impersonateRedirectionUrl: (id: number) => Promise<string | undefined>

    // List of layouts that are protected by the auth module
    protectedLayouts: string[]

    // List of pages that are protected by the auth module
    protectedPages: string[]

    // The path to redirect to after the user logs in
    redirectToAfterLogin: (user: any) => string

    // The path to redirect to if the user is not authenticated
    redirectToIfUnauthenticated: string

    // A dictionnary of functions with the user as parameter (for instance to sign out if the person is not logged in as an admin account)
    shouldRemainAuthFns: Record<string, (user: any) => boolean>
  }
  theme: {
    dark: {
      logo: string
    }
    light: {
      logo: string
    }
  }
  ui: Record<string, any>
}

export default defineAppConfig<CustomAppConfig>({
  auth: {
    guestsLayouts: [],
    guestsPages: ['connexion-provider'],
    // eslint-disable-next-line ts/no-unused-vars
    impersonateRedirectionUrl: async (_id: number) => undefined,
    protectedLayouts: [],
    protectedPages: [],
    redirectToAfterLogin: () => '/',
    redirectToIfUnauthenticated: 'login',
    shouldRemainAuthFns: {},
  },
  theme: {
    dark: {
      logo: '/images/logo_light.svg',
    },
    light: {
      logo: '/images/logo_dark.svg',
    },
  },
  ui: {
    accordion: {
      container: 'w-full flex flex-col border rounded-lg p-2',
      item: {
        color: 'text-gray-900 dark:text-gray-400',
        size: 'text-lg',
      },
      wrapper: 'space-y-4',
    },
    alert: {
      actions: 'flex items-center gap-2 mt-1.5 flex-shrink-0',
      gap: 'gap-3 items-center',

      padding: 'px-4 py-2',
      title: 'text-[1rem]',
    },
    badge: {
      rounded: 'rounded',
      size: {
        '2xs': 'text-[0.6rem] px-1.5 py-0 pb-[0.05rem]',
        '3xs': 'text-[0.6rem] px-1 h-4 py-0 pb-[0.05rem]',
        'xs': 'text-[0.75rem] px-2',
      },
      variant: {
        solid: 'dark:text-white',
      },
    },
    button: {
      base: 'duration-300 transition disabled:opacity-50',
      color: {
        gray: {
          soft: 'dark:bg-gray-900 dark:text-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-800',
        },
      },
      gap: {
        '2xl': 'gap-x-2',
        '2xs': 'gap-x-1',
        'xl': 'gap-x-1',
      },
      icon: {
        size: {
          '2xs': 'h-3 w-3',
        },
      },
      padding: {
        '2xl': 'px-6 py-4',
        '2xs': 'px-2 pt-0.5 pb-[0.16rem]',
      },
      size: {
        '2xl': 'text-xl',
        'xs': 'text-xs leading-tight',
      },
      square: {
        '2xl': 'p-1',
        '2xs': 'p-0.5',
      },
      variant: {
        solid: 'dark:!text-white',
      },
    },
    card: {
      background: 'bg-white dark:bg-gray-800/60',
      base: 'border dark:border-gray-500/30 border-gray-300/60',
      body: { padding: '!py-4 sm:p-5' },
      divide: 'divide-none',
      footer: { padding: '!pt-0 sm:pb-3 sm:px-5' },
      header: { padding: '!pt-3 pb-0 pt-5 sm:px-5' },
      ring: 'ring-0',
      shadow: 'shadow-lg shadow-gray-200/40 dark:shadow-none',
    },
    carousel: {
      arrows: {
        wrapper:
          'arrows--wrapper absolute w-full top-1/2 group-hover/carousel:opacity-100 opacity-0 z-20 duration-300',
      },
      container: 'snap-mandatory snap-x snap-mandatory z-10 sm:z-0',
      default: {
        nextButton: {
          class:
            'rtl:[&_span:last-child]:rotate-180 absolute right-4 top-1/2 transform -translate-y-1/2 rounded-full !bg-white/90 !text-black shadow-sm',
          color: 'black',
          icon: 'mdi:chevron-right',
        },
        prevButton: {
          class:
            'rtl:[&_span:first-child]:rotate-180 absolute left-4 top-1/2 transform -translate-y-1/2 rounded-full !bg-white/90 !text-black shadow-sm',
          color: 'black',
          icon: 'mdi:chevron-left',
        },
      },
      indicators: {
        active: 'h-2.5 w-2.5 bg-white',
        base: 'rounded-full h-2 w-2 hover:bg-primary duration-300',
        inactive: 'bg-white/60 mix-blend-normal',
        wrapper: 'gap-1.5 z-20',
      },
      item: 'basis-full',
    },
    checkbox: {
      base: 'cursor-pointer',
      border:
        'border border-gray-300 dark:border-gray-700 hover:border-brand-500 dark:hover:border-brand-500 duration-200',
      inner: 'ms-1',
      label:
        'data-[size=xl]:text-[1rem] cursor-pointer hover:ml-0.5 duration-300 hover:opacity-80 group-data-[size=xl]/checkbox:text-[1rem]',
    },
    dropdown: {
      arrow: {
        ring: 'before:ring-1 before:ring-gray-400/80 dark:before:ring-gray-700',
      },
      padding: 'd-flex flex-col',
      ring: 'ring-1 ring-gray-300 dark:ring-gray-700 sm:max-w-auto max-w-72 flex flex-col',
      shadow: 'shadow-md',
      width: 'w-auto',
    },
    formGroup: {
      description:
        'text-xs italic -mt-0.5 group-data-[layout=row]:w-48 group-data-[row-gap=xl]:min-w-72 pr-8',
      error: 'mt-0.5 text-xs',
      help: 'mt-1 text-xs',
      label: {
        base: 'text-nowrap text-gray-900 dark:text-gray-100 mb-[1px] -mt-[1px] text-sm font-medium',
        required: 'after:absolute after:top-0 after:-right-3 relative',
        wrapper: 'group-data-[layout=row]:pr-4',
      },
      wrapper: [
        'rounded-md sm:pl-2 sm:-ml-2',
        'mb-3.5',
        'group group/wrapper',
      ].join(' '),
    },
    horizontalNavigation: {
      active:
        'text-white font-normal dark:bg-slate-900 bg-slate-800 dark:text-white after:bg-primary-500 dark:after:bg-primary-400 after:rounded-full after:bg-slate-800',
      after: 'after:h-[0px]',
      base: 'py-1.5 px-3',
      before:
        'rounded-none dark:hover:before:bg-transparent hover:before:bg-transparent',
      icon: {
        active: 'text-white dark:text-gray-100',
        base: '-mr-0.5',
        inactive: 'text-gray-800',
      },
      inactive: 'font-normal dark:text-gray-300 text-gray-800',
    },
    input: {
      base: 'disabled:opacity-[0.9] disabled:!cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800',
      color: {
        white: {
          outline: 'ring-input-border-light dark:ring-input-border-dark',
        },
      },
      file: {
        base: 'px-6 file:bg-gray-100 file:dark:bg-slate-800 file:px-1 file:text-xs file:text-gray-800 file:shadow-sm file:font-normal file:rounded',
        padding: {
          sm: 'ps-[125px]',
        },
      },
      gap: {
        '2xl': 'gap-x-2.5',
      },
      icon: {
        leading: {
          padding: {
            '2xl': 'px-3.5',
          },
        },
        size: {
          '2xl': 'h-8 w-8',
        },
      },
      leading: {
        padding: {
          '2xl': 'ps-14',
        },
      },
      padding: {
        '2xl': 'px-3.5 py-3.5',
      },
      size: {
        '2xl': 'text-base',
        'lg': 'text-base',
        'md': 'text-base',
        'sm': 'text-base',
      },
      trailing: {
        '2xl': 'pe-12',
      },
    },
    notification: {
      background:
        'bg-white bg-gradient-to-r from-white to-zinc-50/50 dark:bg-gray-900',
      container: 'relative overflow-hidden',
      description: 'font-medium leading-5',
      icon: {
        base: 'size-8',
      },
      padding: 'p-5',
      position: 'top-0 right-0',
      ring: 'ring-1 ring-gray-200 dark:ring-gray-800',
      shadow: 'shadow-xl',
      title: 'font-bold text-[1rem] leading-5',
      transition: {
        enterActiveClass: 'transform ease-out duration-500 transition',
        enterFromClass:
          'translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4',
        enterToClass: 'translate-y-0 opacity-100 sm:translate-x-0',
        leaveActiveClass: 'transition ease-in duration-300',
        leaveFromClass: 'opacity-100',
        leaveToClass: 'opacity-0',
      },
    },
    pagination: {},
    primary: 'brand',
    radio: {
      base: 'cursor-pointer ',
      inner: 'ms-0',
      label:
        'cursor-pointer hover:opacity-80 duration-100 py-0.5 ml-0 group-data-[style=native]:py-0 group-data-[style=native]:ml-0.5 group-data-[style=native]:hover:ml-1 group-data-[size=xl]:text-[1rem] group-data-[size=xl]:!py-[2px] pl-1',
      wrapper:
        'group-data-[style=native]:bg-transparent group-data-[style=native]:pl-0 pl-1.5 pr-2 group-data-[style=native]:shadow-none group-data-[style=native]:border-none rounded-full py-0 float-left flex items-center hover:bg-gray-200 dark:hover:bg-gray-800 dark:bg-gray-800 bg-white shadow-sm border border-gray-300 dark:border-gray-700 duration-100 w-auto',
    },

    radioGroup: {
      fieldset:
        'data-[horizontal]:flex data-[horizontal]:flex-row group items-start data-[horizontal]:space-y-0 space-y-1 data-[horizontal]:gap-1.5 data-[vertical]:flex data-[vertical]:flex-col data-[vertical]:items-start data-[vertical]:space-y-0 data-[vertical]:gap-1.5 pb-1',
    },

    select: {
      base: 'cursor-pointer hover:shadow-md duration-200 transition-shadow disabled:opacity-50 disabled:!cursor-not-allowed',
      color: {
        white: {
          outline: 'ring-input-border-light dark:ring-input-border-dark',
        },
      },

      icon: {
        trailing: {
          padding: {
            lg: 'px-1.5',
            md: 'px-1',
            sm: 'pr-1',
            xl: 'px-1.5',
          },
        },
      },
    },

    selectMenu: {
      input:
        'text-[16px] text-gray-800 py-3 placeholder-gray-500 border-gray-300',
      option: {
        base: 'cursor-pointer',
        selected: '!text-emerald-500 font-bold dark:!text-emerald-400',
        selectedIcon: {
          base: 'text-emerald-500 h-4 w-4 dark:text-emerald-400',
        },
      },
      ring: 'ring-1 ring-gray-300 dark:ring-gray-700',
      select: '!cursor-pointer',
      shadow: 'shadow-xl shadow-gray-800/30 dark:shadow-gray-900/90',
    },

    slideover: {
      overlay: {
        background: 'bg-gray-700/75 dark:bg-gray-800/75',
        base: 'backdrop-blur-sm',
      },
      width: 'w-screen sm:max-w-md max-w-80',
    },

    table: {
      checkbox: {
        base: 'bg-transparent',
        padding:
          'bg-transparent !ps-4 w-9 border-b border-gray-100 dark:text-neutral-200 dark:border-gray-600',
      },
      td: {
        base: 'border-b text-neutral-900 text-sm border-gray-100 dark:text-white dark:border-gray-600',
        padding: 'px-3 py-1.5',
      },
      th: {
        base: 'px-2 py-3 text-sm group/thead:border-0 bg-gray-50 text-neutral  dark:text-neutral-200 dark:bg-transparent dark:hover:bg-gray-700 first:pl-3 last:pr-3',
        color: 'text-neutral-950 dark:text-white',
        font: 'font-bold',
        padding: 'px-3 py-3',
      },
      thead: 'bg-gray-50 dark:bg-gray-900/50 *:border-hidden',
      tr: {
        base: ' dark:hover:bg-gray-900/40 hover:bg-slate-100 duration-200',
        selected: 'bg-brand-50/20 dark:!bg-brand-800/20',
      },
      wrapper: 'static overflow-x-auto',
    },
    textarea: {
      color: {
        white: {
          outline: 'ring-input-border-light dark:ring-input-border-dark',
        },
      },
      size: {
        lg: 'text-base',
        md: 'text-base',
        sm: 'text-base',
        xl: 'text-base',
      },
    },
    tooltip: {
      base: 'whitespace-normal !h-auto',
    },

    variables: {
      light: {
        foreground: 'var(--color-gray-950)',
      },
    },
  },
})
